import * as Sentry from '@sentry/browser'
import { useEffect, useState } from 'react'

enum PERMISSION_STATUS {
  GRANTED = 'granted',
  PERMISSION_DENIED_ERROR = 'PermissionDeniedError',
  NOT_ALLOWED_ERROR = 'NotAllowedError',
  NOT_FOUND_ERROR = 'NotFoundError',
  NOT_READABLE_ERROR = 'NotReadableError',
  OVERCONSTRAINED_ERROR = 'OverconstrainedError',
  SECURITY_ERROR = 'SecurityError',
  TYPE_ERROR = 'TypeError'
}

type Permissions = {
  camera: PERMISSION_STATUS | null
  microphone: PERMISSION_STATUS | null
}

export const useMediaPermissions = () => {
  const [permissions, setPermissions] = useState<Permissions>({ camera: null, microphone: null })
  const hasAllPermissionsGranted = permissions.camera === PERMISSION_STATUS.GRANTED && permissions.microphone === PERMISSION_STATUS.GRANTED
  const hasNotAllowedPermissions =
    permissions.camera === PERMISSION_STATUS.NOT_ALLOWED_ERROR || permissions.microphone === PERMISSION_STATUS.NOT_ALLOWED_ERROR

  useEffect(() => {
    if (!hasNotAllowedPermissions) return
    Sentry.captureEvent({
      level: 'warning',
      message: 'User has blocked camera/microphone access',
      extra: {
        permissions
      }
    })
  }, [hasNotAllowedPermissions])

  useEffect(() => {
    checkPermissions()
  }, [])

  const checkPermissions = async () => {
    try {
      await Promise.all([
        // Camera
        navigator.mediaDevices
          .getUserMedia({ video: true })
          .then(() => setPermissions((prev) => ({ ...prev, camera: PERMISSION_STATUS.GRANTED })))
          .catch((err) => setPermissions((prev) => ({ ...prev, camera: err.name }))),

        // Microphone
        navigator.mediaDevices
          .getUserMedia({ audio: true })
          .then(() => setPermissions((prev) => ({ ...prev, microphone: PERMISSION_STATUS.GRANTED })))
          .catch((err) => setPermissions((prev) => ({ ...prev, microphone: err.name })))
      ])
    } catch (err) {
      console.error('Error checking/requesting permissions:', err)
    }
  }

  const getUnblockInstructions = (): string => {
    const isChrome = /Chrome/.test(navigator.userAgent)
    const isSafari = /Safari/.test(navigator.userAgent)
    const isEdge = /Edg/.test(navigator.userAgent)
    const isFirefox = /Firefox/.test(navigator.userAgent)

    if (isChrome) return 'error.camera_microphone.browser.chrome'
    if (isEdge) return 'error.camera_microphone.browser.edge'
    if (isFirefox) return 'error.camera_microphone.browser.firefox'
    if (isSafari) return 'error.camera_microphone.browser.safari'

    return 'error.camera_microphone.browser.chrome'
  }

  return { permissions, getUnblockInstructions, hasAllPermissionsGranted, hasNotAllowedPermissions }
}
